/********************************************************************
 * @author:      Kaven
 * @email:       kaven@wuwenkai.com
 * @website:     http://blog.kaven.xyz
 * @file:        [Kaven-Basic] /src/libs/class/MemoryCache.ts
 * @create:      2018-08-30 16:08:33.658
 * @modify:      2025-07-08 14:32:04.987
 * @version:     6.0.0
 * @times:       181
 * @lines:       194
 * @copyright:   Copyright © 2018-2025 Kaven. All Rights Reserved.
 * @description: [description]
 * @license:
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 ********************************************************************/

import { MillisecondsPerHour, MillisecondsPerMinute } from "../const/TimeFactor";
import { ICache } from "../interface/ICache";
import { DateTime } from "./DateTime";
import { NameValueCache } from "./NameValueCache";

/**
 * @since 4.0.0
 * @version 2021-12-14
 */
export class MemoryCache {
    private readonly cacheMap = new Map<string, ICache>();
    private readonly valueMap = new Map<string, unknown>();

    /**
     *
     * @param name
     * @param value
     * @version 2023-03-27
     * @since 1.0.5
     */
    public SetValue(name: string, value: unknown) {
        this.valueMap.set(name, value);
    }

    /**
     *
     * @param name
     * @param defaultValueIfNotExist
     * @version 2023-03-27
     * @since 1.0.5
     */
    public GetValue<T>(name: string, defaultValueIfNotExist?: T): T | undefined {
        const val = this.valueMap.get(name);
        if (val === undefined) {
            return defaultValueIfNotExist;
        }

        return val as T;
    }

    /**
     *
     * @param name
     * @version 2023-03-27
     * @since 1.0.5
     */
    public RemoveValue(name: string) {
        this.valueMap.delete(name);
    }

    // #endregion

    /**
     *
     * @since 1.0.5
     * @version 2021-12-14
     */
    public RemoveCache(...names: string[]) {
        this.cacheMap.forEach((v, k) => {
            if (v.IsExpired()) {
                names.push(k);
            }
        });

        names.forEach(name => this.cacheMap.delete(name));
    }

    // #region MemoryCache
    /**
     *
     * @param name
     * @param value
     * @param absoluteExpiration
     * @since 1.0.5
     * @version 2021-12-14
     */
    public SetCacheWithDate(
        name: string,
        value: unknown,
        absoluteExpiration: Date,
    ) {
        let item = this.cacheMap.get(name);

        if (item === undefined) {
            item = new NameValueCache(name, value, absoluteExpiration);
            this.cacheMap.set(name, item);
        } else {
            item.value = value;
            item.absoluteExpiration = absoluteExpiration;
        }
    }

    /**
     *
     * @param name
     * @param value
     * @param millisecond
     * @since 1.0.5
     * @version 2021-12-14
     */
    public SetCacheWithMilliseconds(
        name: string,
        value: unknown,
        millisecond: number,
    ) {
        this.SetCacheWithDate(
            name,
            value,
            new DateTime()
                .Add(millisecond)
                .ToDate(),
        );
    }

    /**
     *
     * @param name
     * @param value
     * @param minute
     * @since 1.0.5
     * @version 2021-12-14
     */
    public SetCacheWithMinute(name: string, value: unknown, minute: number) {
        const milliseconds = minute * MillisecondsPerMinute;
        this.SetCacheWithMilliseconds(name, value, milliseconds);
    }

    /**
     *
     * @param name
     * @param value
     * @param hour
     * @since 1.0.5
     * @version 2021-12-14
     */
    public SetCacheWithHour(name: string, value: unknown, hour: number) {
        const milliseconds = hour * MillisecondsPerHour;
        this.SetCacheWithMilliseconds(name, value, milliseconds);
    }

    /**
     *
     * @param name
     * @since 1.0.5
     * @version 2021-12-07
     */
    public GetCache<T>(name: string) {
        const value = this.cacheMap.get(name);

        if (value === undefined) {
            return undefined;
        }

        if (value.IsExpired()) {
            this.cacheMap.delete(name);
            return undefined;
        }

        return value.value as T;
    }
}
